import {
  ITestReport,
  IDiagnosticReport,
  ILessonReport,
  ILevelReport,
  IReportParams,
  IUnitReport,
  IProgressReport
} from "@/models/interfaces/report-generator";

import { getInitialState, updateState } from "@/utils/helpers/StoreUtil";
import DataProvider from "@/utils/helpers/DataProvider";

import { IVuexState } from "@/models/interfaces/store";
import { AxiosResponse, AxiosError } from "axios";

import { State as RootState } from "@/store";
import { ActionTree, MutationTree, Module } from "vuex";

export interface State {
  report: IVuexState<ILessonReport | ILevelReport>;
}

export type Report = ILevelReport | ILessonReport | IUnitReport;

export const actions: ActionTree<State, RootState> = {
  async generateLessonReport({ commit }, data: IReportParams) {
    commit("setReport");

    return DataProvider.get(`schools/${data.schoolId}/reports/lesson`, {
      params: data.params
    })
      .then((response: AxiosResponse) => {
        commit("setReport", response.data);
        return response.data;
      })
      .catch((error: AxiosError) => {
        commit("setReport", error);
        return error;
      });
  },

  async generateLevelReport({ commit }, data: IReportParams) {
    commit("setReport");

    return DataProvider.get(
      `schools/${data.schoolId}/reports/${data.level}/level`,
      {
        params: data.params
      }
    )
      .then((response: AxiosResponse) => {
        commit("setReport", response.data);
        return response.data;
      })
      .catch((error: AxiosError) => {
        commit("setReport", error);
        return error;
      });
  },

  async generateIndividualReport({ commit }, data: IReportParams) {
    commit("setReport");
    const { unit } = data.params;
    const params = unit ? { unit } : {};

    return DataProvider.get(
      `schools/${data.schoolId}/reports/${data.params.userId![0]}/user/${
        data.level
      }/level`,
      {
        params
      }
    )
      .then((response: AxiosResponse) => {
        commit("setReport", response.data);
        return response.data;
      })
      .catch((error: AxiosError) => {
        commit("setReport", error);
        return error;
      });
  },

  async generateUnitReport({ commit }, data: IReportParams) {
    commit("setReport");

    return DataProvider.get(
      `schools/${data.schoolId}/reports/${data.level}/level/${data.unit}/unit`,
      {
        params: data.params
      }
    )
      .then((response: AxiosResponse) => {
        commit("setReport", response.data);
        return response.data;
      })
      .catch((error: AxiosError) => {
        commit("setReport", error);
        return error;
      });
  },

  async generateTestReport({ commit }, data: IReportParams) {
    commit("setReport");

    return DataProvider.get<ITestReport>(
      `schools/${data.schoolId}/reports/assessment`,
      {
        params: data.params
      }
    )
      .then(response => {
        commit("setReport", response.data.userExams);
        return response.data.userExams;
      })
      .catch((error: AxiosError) => {
        commit("setReport", error);
        return error;
      });
  },

  async generateDiagnosticTestReport({ commit }, data: IReportParams) {
    commit("setReport");

    return DataProvider.get<IDiagnosticReport>(
      `schools/${data.schoolId}/reports/diagnostic`,
      {
        params: data.params
      }
    )
      .then(response => {
        commit("setReport", response.data.userExams);
        return response.data.userExams;
      })
      .catch((error: AxiosError) => {
        commit("setReport", error);
        return error;
      });
  },

  async generateBaselineTestReport({ commit }, data: IReportParams) {
    commit("setReport");

    return DataProvider.get<ITestReport>(
      `schools/${data.schoolId}/reports/baseline`,
      {
        params: data.params
      }
    )
      .then(response => {
        commit("setReport", response.data.userExams);
        return response.data.userExams;
      })
      .catch((error: AxiosError) => {
        commit("setReport", error);
        return error;
      });
  },

  async generateProgressReport({ commit }, data: IReportParams) {
    commit("setReport");

    return DataProvider.get<IProgressReport>(
      `schools/${data.schoolId}/reports/progress`,
      {
        params: data.params
      }
    )
      .then(response => {
        commit("setReport", response.data.userExams);
        return response.data.userExams;
      })
      .catch((error: AxiosError) => {
        commit("setReport", error);
        return error;
      });
  }
};

export const mutations: MutationTree<State> = {
  setReport(state, data?: Report | AxiosError) {
    state.report = updateState(state.report, data);
  }
};

const store: Module<State, RootState> = {
  namespaced: true,
  state: {
    report: getInitialState()
  },
  mutations,
  actions
};

export default store;
